import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { MessageSquare, TrendingUp, Eye, Filter, Check, X } from 'lucide-react';

import ContactDialog from '../dialogs/ContactDialog';
import StatusChip from '../StatusChip';

const ContactTab = ({ 
  isLoading, 
  setIsLoading, 
  currentUser, 
  showNotification, 
  logAction
}) => {
  // Contact specific states
  const [contacts, setContacts] = useState([]);
  const [contactsPagination, setContactsPagination] = useState({ page: 0, rowsPerPage: 10, count: 0 });
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactResponse, setContactResponse] = useState('');
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Animation variant
  const slideUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5, 
        ease: "easeOut" 
      } 
    }
  };

  // Load contacts on component mount and when filters change
  useEffect(() => {
    if (currentUser) {
      fetchContacts();
    }
  }, [currentUser, statusFilter, typeFilter, contactsPagination.page, contactsPagination.rowsPerPage]);

  // Get contacts list
  const fetchContacts = async () => {
    setIsLoading(true);
    try {
      // Construct URL with filters
      let url = `/api/admin/contacts?page=${contactsPagination.page}&limit=${contactsPagination.rowsPerPage}`;
      
      // Add status filter if not "all"
      if (statusFilter !== 'all') {
        url += `&responded=${statusFilter === 'respondido' ? '1' : '0'}`;
      }
      
      // Add type filter if not "all"
      if (typeFilter !== 'all') {
        url += `&type=${typeFilter}`;
      }
      
      const response = await axios.get(url);
      setContacts(response.data.contacts);
      setContactsPagination({
        ...contactsPagination,
        count: response.data.total
      });
    } catch (error) {
      console.error('Error al obtener contactos:', error);
      showNotification('Error al cargar contactos', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Change page or rows per page
  const handleContactsPaginationChange = (event, newPage) => {
    setContactsPagination({
      ...contactsPagination,
      page: newPage
    });
  };
  
  const handleContactsRowsPerPageChange = (event) => {
    setContactsPagination({
      ...contactsPagination,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    });
  };
  
  // Apply filters
  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setContactsPagination({
      ...contactsPagination,
      page: 0  // Reset to first page when changing filters
    });
  };
  
  const handleTypeFilterChange = (type) => {
    setTypeFilter(type);
    setContactsPagination({
      ...contactsPagination,
      page: 0  // Reset to first page when changing filters
    });
  };
  
  // Reset filters
  const handleResetFilters = () => {
    setStatusFilter('all');
    setTypeFilter('all');
    setContactsPagination({
      ...contactsPagination,
      page: 0  // Reset to first page when resetting filters
    });
  };
  
  // View contact details
  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    setContactResponse('');
    setContactDialogOpen(true);
    
    // Log action
    logAction(
      'CONTACTO_VISTO',
      `Se ha visto el contacto #${contact.id}`,
      contact.id,
      'contact'
    );
  };
  
  // Format message type for display
  const formatMessageType = (type) => {
    switch(type) {
      case 'consulta': return 'Consulta';
      case 'retroalimentacion': return 'Retroalimentación';
      case 'reporte': return 'Reporte';
      case 'otro': return 'Otro';
      default: return type;
    }
  };

  return (
    <>
      <motion.div 
        className="bg-white rounded-xl shadow-lg overflow-hidden"
        variants={slideUp}
      >
        <div className="p-6 flex justify-between items-center border-b border-gray-100">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-800">Gestión de Contactos</h2>
            {contacts.filter(c => !c.respondido).length > 0 && (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold">
                {contacts.filter(c => !c.respondido).length}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <motion.button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`p-2 ${filtersOpen ? 'bg-indigo-100 text-indigo-700' : 'text-indigo-600 hover:bg-indigo-50'} rounded-full transition-colors`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Filter className="w-5 h-5" />
            </motion.button>
            <motion.button
              onClick={fetchContacts}
              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <TrendingUp className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
        
        {/* Filters Section */}
        <AnimatePresence>
          {filtersOpen && (
            <motion.div 
              className="bg-indigo-50/50 border-b border-indigo-100 p-4"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex flex-wrap items-center gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Estado</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                    className="px-3 py-2 bg-white border border-indigo-200 rounded-lg text-sm text-gray-700 
                              focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">Todos</option>
                    <option value="pendiente">Pendiente</option>
                    <option value="respondido">Respondido</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Tipo</label>
                  <select
                    value={typeFilter}
                    onChange={(e) => handleTypeFilterChange(e.target.value)}
                    className="px-3 py-2 bg-white border border-indigo-200 rounded-lg text-sm text-gray-700 
                              focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">Todos</option>
                    <option value="consulta">Consulta</option>
                    <option value="retroalimentacion">Retroalimentación</option>
                    <option value="reporte">Reporte</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                
                <motion.button
                  onClick={handleResetFilters}
                  className="px-4 py-2 mt-5 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Limpiar filtros
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-indigo-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Asunto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-indigo-800 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {contacts.length > 0 ? (
                contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-indigo-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{contact.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{contact.nombre}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{contact.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatMessageType(contact.tipo_mensaje)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {contact.asunto.length > 30 ? `${contact.asunto.substring(0, 30)}...` : contact.asunto}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{new Date(contact.created_at).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact.respondido ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          Respondido
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                          Pendiente
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <motion.button
                        onClick={() => handleViewContact(contact)}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-100 rounded-full transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Eye className="w-4 h-4" />
                      </motion.button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                    No hay contactos disponibles
                    {(statusFilter !== 'all' || typeFilter !== 'all') && (
                      <span> con los filtros seleccionados</span>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {contactsPagination.count > 0 ? (
              <span>
                Mostrando {contactsPagination.page * contactsPagination.rowsPerPage + 1} a{' '}
                {Math.min((contactsPagination.page + 1) * contactsPagination.rowsPerPage, contactsPagination.count)} de{' '}
                {contactsPagination.count} contactos
                {statusFilter !== 'all' && (
                  <span> ({statusFilter})</span>
                )}
                {typeFilter !== 'all' && (
                  <span> (tipo: {formatMessageType(typeFilter)})</span>
                )}
              </span>
            ) : (
              <span>No hay contactos disponibles</span>
            )}
          </div>
          
          <div className="flex gap-2">
            <select
              value={contactsPagination.rowsPerPage}
              onChange={(e) => handleContactsRowsPerPageChange(e)}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value={5}>5 / página</option>
              <option value={10}>10 / página</option>
              <option value={25}>25 / página</option>
              <option value={50}>50 / página</option>
            </select>
            
            <div className="flex">
              <button
                onClick={() => handleContactsPaginationChange(null, contactsPagination.page - 1)}
                disabled={contactsPagination.page === 0}
                className={`px-3 py-1 border ${
                  contactsPagination.page === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-indigo-600 hover:bg-indigo-50'
                } rounded-l-lg transition-colors`}
              >
                Anterior
              </button>
              <button
                onClick={() => handleContactsPaginationChange(null, contactsPagination.page + 1)}
                disabled={contactsPagination.page >= Math.ceil(contactsPagination.count / contactsPagination.rowsPerPage) - 1}
                className={`px-3 py-1 border border-l-0 ${
                  contactsPagination.page >= Math.ceil(contactsPagination.count / contactsPagination.rowsPerPage) - 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-indigo-600 hover:bg-indigo-50'
                } rounded-r-lg transition-colors`}
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Contact Details Dialog */}
      <ContactDialog 
        contactDialogOpen={contactDialogOpen} 
        setContactDialogOpen={setContactDialogOpen}
        selectedContact={selectedContact}
        contactResponse={contactResponse}
        setContactResponse={setContactResponse}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        showNotification={showNotification}
        logAction={logAction}
        currentUser={currentUser}
        refreshList={fetchContacts}
      />
    </>
  );
};

export default ContactTab;